<template>
  <div class="p-4 md:p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Entrenadores</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400">Administra los entrenadores del gimnasio</p>
      </div>
      <div class="flex items-center gap-2">
        <!-- 🔥 Indicador lector -->
        <div :class="[
          'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold',
          estadoLector
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
            : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500'
        ]">
          <span :class="['w-2 h-2 rounded-full', estadoLector ? 'bg-green-500 animate-pulse' : 'bg-gray-400']"></span>
          <Icon icon="mdi:fingerprint" />
          {{ estadoLector ? 'Lector OK' : 'Sin lector' }}
        </div>

        <button
          @click="openCreateModal"
          class="flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md text-sm"
        >
          <Icon icon="mdi:plus" class="mr-2 text-lg" />
          Nuevo Entrenador
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-12 gap-2">
        <div class="col-span-12 md:col-span-4">
          <label class="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Buscar</label>
          <input v-model="searchQuery" type="text" placeholder="Nombre, apellido, cédula, especialidad..."
            class="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs" />
        </div>
        <div class="col-span-6 md:col-span-3">
          <label class="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Estado</label>
          <select v-model="filterEstado" class="w-full border rounded-lg px-2.5 py-1.5 text-xs dark:bg-gray-700 dark:border-gray-600">
            <option value="all">Todos</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
            <option value="vacaciones">Vacaciones</option>
          </select>
        </div>
        <div class="col-span-6 md:col-span-3">
          <label class="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Web</label>
          <select v-model="filterWeb" class="w-full border rounded-lg px-2.5 py-1.5 text-xs dark:bg-gray-700 dark:border-gray-600">
            <option value="all">Todos</option>
            <option value="visible">Visibles en web</option>
            <option value="oculto">Ocultos en web</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Lista -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div v-if="loading" class="py-16 text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p class="text-gray-600 dark:text-gray-400">Cargando entrenadores...</p>
      </div>

      <div v-else-if="filteredEntrenadores.length === 0" class="py-16 text-center">
        <Icon icon="mdi:dumbbell" class="text-5xl text-gray-400 mb-4" />
        <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">No hay entrenadores</h3>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Registra el primer entrenador</p>
        <button @click="openCreateModal" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
          <Icon icon="mdi:plus" class="mr-2 inline" /> Registrar Entrenador
        </button>
      </div>

      <div v-else class="p-4 md:p-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <div v-for="e in filteredEntrenadores" :key="e.idEntrenador"
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all overflow-hidden">

            <!-- Foto -->
            <div class="relative h-48 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center">
              <span :class="['absolute top-2 right-2 z-10 px-2 py-1 text-xs font-semibold rounded-full shadow-sm', getEstadoClass(e.estado)]">
                {{ formatearEstadoEntrenador(e.estado) }}
              </span>

              <div v-if="!e.mostrar_en_web" class="absolute top-2 left-2 z-10 px-2 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                <Icon icon="mdi:eye-off" class="inline mr-1" /> Oculto web
              </div>

              <!-- 🔥 Indicador de huella registrada -->
              <div v-if="e.tieneHuella" 
                class="absolute bottom-2 right-2 z-10 px-2 py-1 text-xs font-bold rounded-full bg-purple-100 text-purple-700 shadow-sm flex items-center gap-1">
                <Icon icon="mdi:fingerprint" /> Huella
              </div>

              <img
                v-if="e.foto_perfil"
                :src="obtenerUrlFotoEntrenador(e.foto_perfil)"
                :alt="e.nombre"
                class="w-full h-full object-cover"
                @error="onImageError"
              />
              <div v-else
                class="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg"
                :style="{ backgroundColor: obtenerColorAvatarEntrenador(e.nombre + ' ' + e.apellido) }">
                {{ obtenerInicialesEntrenador(e) }}
              </div>
            </div>

            <div class="p-4">
              <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">
                {{ obtenerNombreCompletoEntrenador(e) }}
              </h3>
              <p v-if="e.cedula" class="text-xs text-gray-500 mb-1">
                <Icon icon="mdi:card-account-details" class="inline mr-1" /> {{ e.cedula }}
              </p>
              <p v-if="e.especialidades" class="text-xs text-blue-600 dark:text-blue-400 mb-2 line-clamp-2">
                <Icon icon="mdi:star" class="inline mr-1" /> {{ e.especialidades }}
              </p>

              <!-- Redes -->
              <div class="flex flex-wrap gap-1.5 mb-3">
                <a v-if="e.whatsapp" :href="construirLinkWhatsapp(e.whatsapp)" target="_blank"
                  class="px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200">
                  <Icon icon="mdi:whatsapp" class="inline" />
                </a>
                <a v-if="e.instagram" :href="construirLinkInstagram(e.instagram)" target="_blank"
                  class="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs hover:bg-pink-200">
                  <Icon icon="mdi:instagram" class="inline" />
                </a>
                <a v-if="e.tiktok" :href="construirLinkTikTok(e.tiktok)" target="_blank"
                  class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200">
                  <Icon icon="mdi:music-note" class="inline" />
                </a>
                <a v-if="e.facebook" :href="construirLinkFacebook(e.facebook)" target="_blank"
                  class="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">
                  <Icon icon="mdi:facebook" class="inline" />
                </a>
              </div>

              <!-- Tarifas -->
              <div v-if="e.tarifa_sesion_usd || e.tarifa_mensual_usd" class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                <span v-if="e.tarifa_sesion_usd">Sesión: ${{ e.tarifa_sesion_usd }}</span>
                <span v-if="e.tarifa_mensual_usd" class="ml-2">Mensual: ${{ e.tarifa_mensual_usd }}</span>
              </div>

              <!-- Acciones -->
              <div class="flex justify-end space-x-1 pt-3 border-t border-gray-200 dark:border-gray-700">
                <!-- 🔥 HUELLA -->
                <button
                  @click="capturarHuellaEntrenador(e)"
                  :disabled="!estadoLector || !e.idUsuario"
                  :class="[
                    'p-2 rounded-lg transition-all',
                    e.tieneHuella
                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      : 'bg-purple-50 text-purple-600 hover:bg-purple-100',
                    (!estadoLector || !e.idUsuario) ? 'opacity-50 cursor-not-allowed' : ''
                  ]"
                  :title="e.tieneHuella ? 'Re-registrar huella' : 'Registrar huella'"
                >
                  <Icon :icon="e.tieneHuella ? 'mdi:fingerprint' : 'mdi:fingerprint'" />
                </button>

                <button @click="openEditModal(e)" class="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100" title="Editar">
                  <Icon icon="mdi:pencil" />
                </button>
                <button @click="confirmDelete(e)" class="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100" title="Desactivar">
                  <Icon icon="mdi:delete" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR (tu original sin cambios) -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">
              {{ editing ? 'Editar Entrenador' : 'Nuevo Entrenador' }}
            </h3>
            <button @click="closeModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>

          <form @submit.prevent="saveEntrenador" class="px-6 py-4 overflow-y-auto max-h-[calc(90vh-130px)]">
            <!-- Foto -->
            <div class="flex flex-col items-center mb-6">
              <div class="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer relative"
                @click="$refs.fileInput.click()">
                <img v-if="previewUrl" :src="previewUrl" class="w-full h-full object-cover" />
                <Icon v-else icon="mdi:account-circle" class="text-7xl text-gray-400" />
              </div>
              <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handleFileChange" />
              <button v-if="previewUrl && selectedFile" type="button" @click="removeSelectedFile"
                class="mt-2 text-xs text-red-600 hover:underline">Quitar foto</button>
            </div>

            <!-- Datos -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="md:col-span-2 relative">
                <label class="block text-sm font-medium mb-1">Usuario del sistema *</label>
                <div class="w-full px-3 py-2 border-2 rounded-xl text-sm flex items-center gap-2 transition-colors"
                  :class="usuarioSeleccionado ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600' : 'dark:bg-gray-700 dark:border-gray-600'">
                  <Icon :icon="usuarioSeleccionado ? 'mdi:account-check' : 'mdi:account-search'"
                    :class="usuarioSeleccionado ? 'text-blue-600' : 'text-gray-400'" />
                  <span v-if="usuarioSeleccionado" class="flex-1 text-blue-800 dark:text-blue-200 font-medium truncate">
                    {{ usuarioSeleccionado.nombre }} {{ usuarioSeleccionado.apellido }}
                    <span class="text-blue-500 font-normal">(@{{ usuarioSeleccionado.nombreUsuario }})</span>
                  </span>
                  <input v-else ref="usuarioInput" v-model="usuarioSearch" type="text"
                    placeholder="Escribe nombre, apellido o usuario..." :disabled="!!editing"
                    class="flex-1 bg-transparent outline-none disabled:cursor-not-allowed"
                    @focus="showUsuarioDropdown = true" @input="showUsuarioDropdown = true" @blur="delayHideUsuarioDropdown" />
                  <button v-if="usuarioSeleccionado && !editing" type="button" @click.stop="clearUsuario"
                    class="text-gray-400 hover:text-red-500 transition-colors" title="Cambiar usuario">
                    <Icon icon="mdi:close-circle" class="text-lg" />
                  </button>
                  <Icon v-if="usuarioSeleccionado && editing" icon="mdi:lock" class="text-gray-400"
                    title="No se puede cambiar el usuario al editar" />
                </div>
                <p v-if="!usuarioSeleccionado" class="text-xs text-gray-500 mt-1">Debe ser un usuario ya creado en el sistema</p>

                <div v-if="showUsuarioDropdown && !editing && !usuarioSeleccionado"
                  class="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                  <div v-if="usuariosFiltrados.length === 0" class="px-3 py-2 text-xs text-gray-500">No se encontraron usuarios</div>
                  <button v-for="u in usuariosFiltrados" :key="u.idUsuario" type="button" @mousedown.prevent="seleccionarUsuario(u)"
                    class="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-2">
                    <img v-if="u.fotoPerfil" :src="getImagenUsuario(u.fotoPerfil)" class="w-6 h-6 rounded-full object-cover" />
                    <div v-else class="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">
                      {{ (u.nombre || '?')[0] }}{{ (u.apellido || '?')[0] }}
                    </div>
                    <span>{{ u.nombre }} {{ u.apellido }}</span>
                    <span class="text-xs text-gray-500">@{{ u.nombreUsuario }}</span>
                  </button>
                </div>
              </div>

              <div class="md:col-span-2 relative">
                <label class="block text-sm font-medium mb-1">Sede</label>
                <div class="w-full px-3 py-2 border-2 rounded-xl text-sm flex items-center gap-2 transition-colors"
                  :class="sedeSeleccionada ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600' : 'dark:bg-gray-700 dark:border-gray-600'">
                  <Icon :icon="sedeSeleccionada ? 'mdi:check-circle' : 'mdi:office-building'"
                    :class="sedeSeleccionada ? 'text-blue-600' : 'text-gray-400'" />
                  <span v-if="sedeSeleccionada" class="flex-1 text-blue-800 dark:text-blue-200 font-medium truncate">
                    {{ sedeSeleccionada.nombre }}
                  </span>
                  <input v-else ref="sedeInput" v-model="sedeSearch" type="text" placeholder="Escribe el nombre de la sede..."
                    class="flex-1 bg-transparent outline-none" @focus="showSedeDropdown = true" @input="showSedeDropdown = true" @blur="delayHideSedeDropdown" />
                  <button v-if="sedeSeleccionada" type="button" @click.stop="clearSede"
                    class="text-gray-400 hover:text-red-500 transition-colors" title="Quitar sede">
                    <Icon icon="mdi:close-circle" class="text-lg" />
                  </button>
                </div>
                <p v-if="!sedeSeleccionada" class="text-xs text-gray-500 mt-1">Opcional — puedes dejarlo sin sede</p>

                <div v-if="showSedeDropdown && !sedeSeleccionada"
                  class="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                  <button type="button" @mousedown.prevent="seleccionarSede(null)"
                    class="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-500 italic">
                    <Icon icon="mdi:close" class="inline mr-1" /> Sin sede
                  </button>
                  <div v-if="sedesFiltradas.length === 0" class="px-3 py-2 text-xs text-gray-500">No se encontraron sedes</div>
                  <button v-for="s in sedesFiltradas" :key="s.idSede" type="button" @mousedown.prevent="seleccionarSede(s)"
                    class="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30">
                    {{ s.nombre }}
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Nombre *</label>
                <input v-model="form.nombre" required maxlength="100"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Apellido *</label>
                <input v-model="form.apellido" required maxlength="100"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Cédula</label>
                <input v-model="form.cedula" maxlength="20"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Teléfono</label>
                <input v-model="form.telefono" maxlength="20"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-1">Email</label>
                <input v-model="form.email" type="email" maxlength="150"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-1">Biografía</label>
                <textarea v-model="form.biografia" rows="3"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm"></textarea>
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-1">Frase motivacional</label>
                <input v-model="form.frase_motivacional" maxlength="255"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Años de experiencia</label>
                <input v-model.number="form.anios_experiencia" type="number" min="0"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Especialidades (separadas por coma)</label>
                <input v-model="form.especialidades" maxlength="255" placeholder="CrossFit, Yoga, Nutrición"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">WhatsApp</label>
                <input v-model="form.whatsapp" placeholder="+584141234567"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Instagram</label>
                <input v-model="form.instagram" placeholder="@usuario"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">TikTok</label>
                <input v-model="form.tiktok" placeholder="@usuario"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Facebook</label>
                <input v-model="form.facebook" placeholder="usuario o URL"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Tarifa sesión USD</label>
                <input v-model.number="form.tarifa_sesion_usd" type="number" step="0.01" min="0"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Tarifa mensual USD</label>
                <input v-model.number="form.tarifa_mensual_usd" type="number" step="0.01" min="0"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Comisión (%)</label>
                <input v-model.number="form.comision_porcentaje" type="number" step="0.01" min="0" max="100"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Fecha contratación</label>
                <input v-model="form.fecha_contratacion" type="date"
                  class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">Estado</label>
                <select v-model="form.estado" class="w-full px-3 py-2 border-2 rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm">
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="vacaciones">Vacaciones</option>
                </select>
              </div>

              <div class="flex items-center md:col-span-2">
                <input type="checkbox" id="mostrar_en_web" v-model="form.mostrar_en_web" class="w-4 h-4" />
                <label for="mostrar_en_web" class="ml-2 text-sm">Mostrar en la web pública</label>
              </div>
            </div>

            <div class="mt-6 flex justify-end space-x-3">
              <button type="button" @click="closeModal"
                class="px-4 py-2.5 border-2 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancelar
              </button>
              <button type="submit" :disabled="saving"
                class="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm disabled:opacity-50">
                <Icon v-if="saving" icon="mdi:loading" class="animate-spin mr-2 inline" />
                {{ saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- ⭐ MODAL DE CAPTURA DE HUELLA -->
    <Teleport to="body">
      <div v-if="capturaActiva" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10000] px-4">
        <div class="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-8 shadow-2xl text-center">

          <!-- Fase 1: esperando -->
          <template v-if="faseCaptura === 'esperando'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="absolute inset-0 bg-purple-500/20 rounded-full animate-ping"></div>
              <div class="relative bg-purple-500 rounded-full w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:fingerprint" class="text-white text-6xl" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">Coloca tu dedo en el lector</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Apoya el dedo con firmeza, sin moverlo</p>
            <p v-if="entrenadorParaHuella" class="text-xs text-purple-500 mt-3 font-medium">
              Registrando para: {{ entrenadorParaHuella.nombre }} {{ entrenadorParaHuella.apellido }}
            </p>
          </template>

          <!-- Fase 2: procesando -->
          <template v-else-if="faseCaptura === 'procesando'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="absolute inset-0 rounded-full border-4 border-amber-200 dark:border-amber-900"></div>
              <div class="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin"></div>
              <div class="relative w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:fingerprint" class="text-amber-500 text-6xl" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">Verificando huella...</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">No retires el dedo todavía</p>
          </template>

          <!-- Fase 3: éxito -->
          <template v-else-if="faseCaptura === 'exito'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
              <div class="relative bg-green-500 rounded-full w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:check-bold" class="text-white text-6xl" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">¡Huella registrada!</h3>
            <p class="text-sm text-green-600 dark:text-green-400 mt-1">
              {{ resultadoCaptura?.mensaje || 'Huella capturada correctamente' }}
            </p>
            <p v-if="entrenadorParaHuella" class="text-xs text-gray-500 mt-2">
              Entrenador: {{ entrenadorParaHuella.nombre }} {{ entrenadorParaHuella.apellido }}
            </p>
          </template>

          <!-- Fase 4: error -->
          <template v-else-if="faseCaptura === 'error'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="relative bg-red-500 rounded-full w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:close-thick" class="text-white text-6xl" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">No se pudo procesar</h3>
            <p class="text-sm text-red-500 mt-1">{{ mensajeError }}</p>
            <button @click="reintentarCaptura"
              class="mt-4 px-5 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm">
              Intentar de nuevo
            </button>
          </template>

          <button v-if="faseCaptura !== 'procesando'" @click="cancelarCaptura"
            class="mt-6 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            Cancelar
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import * as signalR from '@microsoft/signalr';

import {
  obtenerEntrenadores,
  crearEntrenador,
  actualizarEntrenador,
  eliminarEntrenador,
  obtenerUrlFotoEntrenador,
  obtenerNombreCompletoEntrenador,
  obtenerInicialesEntrenador,
  obtenerColorAvatarEntrenador,
  formatearEstadoEntrenador,
  construirLinkWhatsapp,
  construirLinkInstagram,
  construirLinkTikTok,
  construirLinkFacebook
} from '@/api/entrenadores.js';

import { obtenerUsuarios, obtenerUrlImagen, usuarioTieneHuella } from '@/api/usuarios.js';
import { obtenerSedes } from '@/api/sede.js';
import { obtenerEstadoLector, activarModoRegistro } from '@/api/huella';
import { getCurrentApiUrl } from '@/api/index.js';

export default {
  name: 'EntrenadoresList',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      entrenadores: [],
      usuariosDisponibles: [],
      sedes: [],
      loading: false,
      saving: false,
      showModal: false,
      editing: null,
      searchQuery: '',
      filterEstado: 'all',
      filterWeb: 'all',
      selectedFile: null,
      previewUrl: null,

      usuarioSearch: '',
      showUsuarioDropdown: false,
      usuarioSeleccionado: null,

      sedeSearch: '',
      showSedeDropdown: false,
      sedeSeleccionada: null,

      form: this.getEmptyForm(),

      // 🔥 Huella
      estadoLector: false,
      capturaActiva: false,
      faseCaptura: 'esperando',
      resultadoCaptura: null,
      mensajeError: '',
      entrenadorParaHuella: null,
      hubConnection: null,
      timeoutEsperaId: null
    };
  },
  computed: {
    filteredEntrenadores() {
      let list = [...this.entrenadores];

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(e =>
          (e.nombre || '').toLowerCase().includes(q) ||
          (e.apellido || '').toLowerCase().includes(q) ||
          (e.cedula || '').toLowerCase().includes(q) ||
          (e.especialidades || '').toLowerCase().includes(q)
        );
      }

      if (this.filterEstado !== 'all') {
        list = list.filter(e => (e.estado || '').toLowerCase() === this.filterEstado);
      }

      if (this.filterWeb === 'visible') {
        list = list.filter(e => e.mostrar_en_web === true);
      } else if (this.filterWeb === 'oculto') {
        list = list.filter(e => e.mostrar_en_web === false);
      }

      return list;
    },

    usuariosFiltrados() {
      const q = (this.usuarioSearch || '').trim().toLowerCase();
      if (!q) return this.usuariosDisponibles.slice(0, 20);
      return this.usuariosDisponibles.filter(u =>
        (u.nombre || '').toLowerCase().includes(q) ||
        (u.apellido || '').toLowerCase().includes(q) ||
        (u.nombreUsuario || '').toLowerCase().includes(q)
      ).slice(0, 20);
    },

    sedesFiltradas() {
      const q = (this.sedeSearch || '').trim().toLowerCase();
      if (!q) return this.sedes.slice(0, 20);
      return this.sedes.filter(s => (s.nombre || '').toLowerCase().includes(q)).slice(0, 20);
    }
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadUsuarios(), this.loadSedes()]);
    await this.conectarSignalR();
    await this.cargarEstadoHuellas();
  },
  beforeUnmount() {
    this.limpiarTimeoutEspera();
    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  },
  methods: {
    obtenerUrlFotoEntrenador,
    obtenerNombreCompletoEntrenador,
    obtenerInicialesEntrenador,
    obtenerColorAvatarEntrenador,
    formatearEstadoEntrenador,
    construirLinkWhatsapp,
    construirLinkInstagram,
    construirLinkTikTok,
    construirLinkFacebook,

    getImagenUsuario(nombreArchivo) {
      return obtenerUrlImagen(nombreArchivo);
    },

    getEmptyForm() {
      return {
        idUsuario: '',
        idSede: null,
        nombre: '',
        apellido: '',
        cedula: '',
        telefono: '',
        email: '',
        biografia: '',
        frase_motivacional: '',
        anios_experiencia: null,
        whatsapp: '',
        instagram: '',
        tiktok: '',
        facebook: '',
        especialidades: '',
        tarifa_sesion_usd: null,
        tarifa_sesion_bs: null,
        tarifa_mensual_usd: null,
        tarifa_mensual_bs: null,
        comision_porcentaje: 0,
        fecha_contratacion: '',
        estado: 'activo',
        mostrar_en_web: true
      };
    },

    async loadData() {
      this.loading = true;
      try {
        const res = await obtenerEntrenadores();
        if (res.success) {
          this.entrenadores = res.data || [];
          // 🔥 Cargar huellas
          await this.cargarEstadoHuellas();
        } else {
          this.toast.error(res.error);
        }
      } finally {
        this.loading = false;
      }
    },

    async loadUsuarios() {
      try {
        const res = await obtenerUsuarios('activos');
        if (res.success) this.usuariosDisponibles = res.data || [];
      } catch (e) {
        console.warn(e);
      }
    },

    async loadSedes() {
      try {
        const res = await obtenerSedes();
        if (res?.success) this.sedes = res.data || [];
      } catch (e) {
        console.warn('No se pudieron cargar las sedes:', e);
      }
    },

    // 🔥 Cargar estado de huellas de todos los entrenadores
    async cargarEstadoHuellas() {
      try {
        const res = await obtenerUsuariosConHuella?.();
        // Opcional: usar endpoint específico o consultar uno por uno
        // Por ahora, marcamos sin huella por defecto
        this.entrenadores = this.entrenadores.map(e => ({
          ...e,
          tieneHuella: false  // se actualiza al registrar
        }));
      } catch (e) {
        console.warn('No se pudo cargar estado de huellas:', e);
      }
    },

    // ========== SIGNALR ==========
    async conectarSignalR() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const HUB_URL = `${getCurrentApiUrl()}/asistenciaHub`;

      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, {
          accessTokenFactory: () => token || '',
          transport: signalR.HttpTransportType.WebSocket | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect()
        .build();

      this.hubConnection.on('EstadoLector', (data) => {
        this.estadoLector = !!data?.conectado;
      });

      this.hubConnection.on('HuellaProcesando', () => {
        if (this.capturaActiva) {
          this.limpiarTimeoutEspera();
          this.faseCaptura = 'procesando';
        }
      });

      this.hubConnection.on('HuellaDetectada', (data) => {
        this.manejarResultadoHuella(data);
      });

      this.hubConnection.on('HuellaError', (data) => {
        if (this.capturaActiva) {
          this.limpiarTimeoutEspera();
          this.faseCaptura = 'error';
          this.mensajeError = data?.mensaje || 'Error al leer la huella';
        }
      });

      try {
        await this.hubConnection.start();
        console.log('✅ SignalR conectado (EntrenadoresList)');
        try {
          const res = await obtenerEstadoLector();
          if (res.success && res.data) {
            this.estadoLector = res.data.conectado || false;
          }
        } catch (_) {}
      } catch (e) {
        console.error('❌ SignalR:', e);
      }
    },

    manejarResultadoHuella(data) {
      if (!this.capturaActiva) return;
      this.limpiarTimeoutEspera();

      if (data?.exito) {
        this.faseCaptura = 'exito';
        this.resultadoCaptura = data;

        // 🔥 Marcar entrenador como con huella
        if (this.entrenadorParaHuella) {
          const idx = this.entrenadores.findIndex(e => e.idEntrenador === this.entrenadorParaHuella.idEntrenador);
          if (idx !== -1) {
            this.entrenadores[idx] = { ...this.entrenadores[idx], tieneHuella: true };
          }
        }

        setTimeout(() => {
          if (this.faseCaptura === 'exito') {
            this.capturaActiva = false;
            this.entrenadorParaHuella = null;
          }
        }, 2500);
      } else {
        this.faseCaptura = 'error';
        this.mensajeError = data?.mensaje || 'Huella no reconocida';
      }
    },

    limpiarTimeoutEspera() {
      if (this.timeoutEsperaId) {
        clearTimeout(this.timeoutEsperaId);
        this.timeoutEsperaId = null;
      }
    },

    // ========== CAPTURA DE HUELLA ==========
    async capturarHuellaEntrenador(entrenador) {
      if (!this.estadoLector) {
        this.toast.error('❌ El lector FS88H no está conectado');
        return;
      }

      if (!entrenador.idUsuario) {
        this.toast.error('Este entrenador no tiene usuario vinculado');
        return;
      }

      this.entrenadorParaHuella = entrenador;
      this.capturaActiva = true;
      this.faseCaptura = 'esperando';
      this.resultadoCaptura = null;
      this.mensajeError = '';

      try {
        const res = await activarModoRegistro(entrenador.idUsuario);
        if (!res.success || !res.data?.exito) {
          this.faseCaptura = 'error';
          this.mensajeError = res.data?.mensaje || 'No se pudo activar el modo registro';
          return;
        }

        this.timeoutEsperaId = setTimeout(() => {
          if (this.faseCaptura === 'esperando') {
            this.toast.info('Sigue esperando... aún no se detecta ningún dedo');
          }
        }, 20000);
      } catch (e) {
        console.error(e);
        this.faseCaptura = 'error';
        this.mensajeError = e.message || 'Error al activar modo registro';
      }
    },

    cancelarCaptura() {
      this.limpiarTimeoutEspera();
      this.capturaActiva = false;
      this.faseCaptura = 'esperando';
      this.resultadoCaptura = null;
      this.entrenadorParaHuella = null;
    },

    reintentarCaptura() {
      if (this.entrenadorParaHuella) {
        this.capturarHuellaEntrenador(this.entrenadorParaHuella);
      }
    },

    // ========== BUSCADOR USUARIO ==========
    focusUsuarioInput() {
      if (this.editing || this.usuarioSeleccionado) return;
      this.$refs.usuarioInput?.focus();
    },

    seleccionarUsuario(u) {
      this.usuarioSeleccionado = u;
      this.form.idUsuario = u.idUsuario;
      this.usuarioSearch = '';
      this.showUsuarioDropdown = false;

      if (!this.editing) {
        if (!this.form.nombre) this.form.nombre = u.nombre || '';
        if (!this.form.apellido) this.form.apellido = u.apellido || '';
        if (!this.form.email) this.form.email = u.email || '';
        if (!this.form.telefono) this.form.telefono = u.telefono || '';
      }
    },

    clearUsuario() {
      this.usuarioSeleccionado = null;
      this.form.idUsuario = '';
      this.usuarioSearch = '';
      this.$nextTick(() => {
        this.$refs.usuarioInput?.focus();
      });
    },

    delayHideUsuarioDropdown() {
      setTimeout(() => { this.showUsuarioDropdown = false; }, 150);
    },

    // ========== BUSCADOR SEDE ==========
    focusSedeInput() {
      if (this.sedeSeleccionada) return;
      this.$refs.sedeInput?.focus();
    },

    seleccionarSede(s) {
      this.sedeSeleccionada = s;
      this.form.idSede = s ? s.idSede : null;
      this.sedeSearch = '';
      this.showSedeDropdown = false;
    },

    clearSede() {
      this.sedeSeleccionada = null;
      this.form.idSede = null;
      this.sedeSearch = '';
      this.$nextTick(() => {
        this.$refs.sedeInput?.focus();
      });
    },

    delayHideSedeDropdown() {
      setTimeout(() => { this.showSedeDropdown = false; }, 150);
    },

    // ========== MODAL ==========
    openCreateModal() {
      this.editing = null;
      this.form = this.getEmptyForm();
      this.selectedFile = null;
      this.previewUrl = null;
      this.usuarioSeleccionado = null;
      this.usuarioSearch = '';
      this.sedeSeleccionada = null;
      this.sedeSearch = '';
      this.showModal = true;
    },

    openEditModal(e) {
      this.editing = e;
      this.form = {
        idUsuario: e.idUsuario,
        idSede: e.idSede,
        nombre: e.nombre || '',
        apellido: e.apellido || '',
        cedula: e.cedula || '',
        telefono: e.telefono || '',
        email: e.email || '',
        biografia: e.biografia || '',
        frase_motivacional: e.frase_motivacional || '',
        anios_experiencia: e.anios_experiencia ?? null,
        whatsapp: e.whatsapp || '',
        instagram: e.instagram || '',
        tiktok: e.tiktok || '',
        facebook: e.facebook || '',
        especialidades: e.especialidades || '',
        tarifa_sesion_usd: e.tarifa_sesion_usd ?? null,
        tarifa_sesion_bs: e.tarifa_sesion_bs ?? null,
        tarifa_mensual_usd: e.tarifa_mensual_usd ?? null,
        tarifa_mensual_bs: e.tarifa_mensual_bs ?? null,
        comision_porcentaje: e.comision_porcentaje ?? 0,
        fecha_contratacion: e.fecha_contratacion ? e.fecha_contratacion.substring(0, 10) : '',
        estado: e.estado || 'activo',
        mostrar_en_web: e.mostrar_en_web !== false
      };

      this.usuarioSeleccionado = this.usuariosDisponibles.find(u => u.idUsuario === e.idUsuario) || {
        idUsuario: e.idUsuario,
        nombre: e.nombre,
        apellido: e.apellido,
        nombreUsuario: '—'
      };
      this.sedeSeleccionada = this.sedes.find(s => s.idSede === e.idSede) || null;
      this.usuarioSearch = '';
      this.sedeSearch = '';

      this.previewUrl = e.foto_perfil ? obtenerUrlFotoEntrenador(e.foto_perfil) : null;
      this.selectedFile = null;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editing = null;
      this.selectedFile = null;
      this.previewUrl = null;
      this.usuarioSeleccionado = null;
      this.usuarioSearch = '';
      this.sedeSeleccionada = null;
      this.sedeSearch = '';
      this.form = this.getEmptyForm();
    },

    handleFileChange(evt) {
      const file = evt.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) return this.toast.error('Máx 5MB');
      if (!file.type.startsWith('image/')) return this.toast.error('Debe ser imagen');
      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
    },

    removeSelectedFile() {
      this.selectedFile = null;
      this.previewUrl = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    async saveEntrenador() {
      if (!this.form.idUsuario) {
        return this.toast.error('Debes seleccionar un usuario');
      }
      if (!this.form.nombre || !this.form.apellido) {
        return this.toast.error('Nombre y apellido son obligatorios');
      }

      this.saving = true;
      try {
        const payload = { ...this.form, foto: this.selectedFile };

        let res;
        if (this.editing) {
          res = await actualizarEntrenador(this.editing.idEntrenador, payload);
        } else {
          res = await crearEntrenador(payload);
        }

        if (!res.success) throw new Error(res.error);

        this.toast.success(this.editing ? 'Entrenador actualizado' : 'Entrenador creado');
        await this.loadData();
        this.closeModal();
      } catch (err) {
        this.toast.error(err.message);
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete(e) {
      if (!confirm(`¿Desactivar a ${e.nombre} ${e.apellido}?`)) return;
      try {
        const res = await eliminarEntrenador(e.idEntrenador);
        if (res.success) {
          this.toast.success('Entrenador desactivado');
          await this.loadData();
        } else {
          this.toast.error(res.error);
        }
      } catch (err) {
        this.toast.error(err.message);
      }
    },

    getEstadoClass(estado) {
      const e = (estado || '').toLowerCase();
      if (e === 'activo') return 'bg-green-100 text-green-800';
      if (e === 'inactivo') return 'bg-gray-100 text-gray-700';
      if (e === 'vacaciones') return 'bg-amber-100 text-amber-700';
      return 'bg-gray-100 text-gray-700';
    },

    onImageError(evt) {
      evt.target.style.display = 'none';
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>